import { Box, Typography } from "@mui/material";
import type { ExperienceJourney } from "../campaignTypes";
import {
  PORTAL_MAX_WIDTH,
  bodySx,
  eyebrowSx,
  portalFont,
  sectionTitleSx,
} from "../partnerTheme";

export default function ExperienceJourneySection({
  journey,
}: {
  journey: ExperienceJourney;
}) {
  const sectionId = journey.id || "experience";

  return (
    <Box
      id={sectionId}
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        pt: { xs: 7, md: 10 },
        pb: { xs: 2, md: 4 },
        scrollMarginTop: 96,
      }}
    >
      <Typography sx={{ ...eyebrowSx, fontSize: { xs: "0.75rem", md: "0.8125rem" } }}>
        {journey.eyebrow}
      </Typography>
      <Typography
        sx={{
          ...sectionTitleSx,
          fontSize: { xs: "1.85rem", md: "2.75rem" },
          mb: { xs: 2, md: 2.5 },
        }}
      >
        {journey.title}
      </Typography>
      <Typography
        sx={{
          ...bodySx,
          maxWidth: 760,
          mb: { xs: 4, md: 6 },
          fontSize: { xs: "1rem", md: "1.125rem" },
          color: "var(--portal-text-muted)",
        }}
      >
        {journey.summary}
      </Typography>

      <Box sx={{ display: "grid", gap: { xs: 4, md: 7 } }}>
        {journey.beats.map((beat, index) => {
          const imageOnRight = index % 2 === 0;
          return (
            <Box
              key={beat.id}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: beat.imageSrc ? "1fr 1fr" : "1fr",
                },
                gap: { xs: 2.5, md: 4 },
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  order: { xs: 2, md: imageOnRight ? 1 : 2 },
                  py: { md: 1 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: portalFont,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "var(--portal-accent)",
                    mb: 1.25,
                  }}
                >
                  {beat.number}
                </Typography>
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: portalFont,
                    fontWeight: 700,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                    color: "var(--portal-text)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.15,
                    mb: 1.5,
                  }}
                >
                  {beat.title}
                </Typography>
                <Typography
                  sx={{
                    ...bodySx,
                    color: "var(--portal-text-muted)",
                    fontSize: { xs: "0.975rem", md: "1.0625rem" },
                    maxWidth: 520,
                  }}
                >
                  {beat.body}
                </Typography>
              </Box>

              {beat.imageSrc ? (
                <Box
                  sx={{
                    order: { xs: 1, md: imageOnRight ? 2 : 1 },
                    position: "relative",
                    borderRadius: { xs: "20px", md: "28px" },
                    overflow: "hidden",
                    border: "1px solid var(--portal-border)",
                    aspectRatio: { xs: "16 / 11", md: "4 / 3" },
                    bgcolor: "var(--portal-surface)",
                  }}
                >
                  <Box
                    component="img"
                    src={beat.imageSrc}
                    alt={beat.imageAlt || beat.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <Box
                    aria-hidden
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(180deg, transparent 55%, rgba(10,10,10,0.35) 100%)",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
